<template>
  <NuxtLink
    v-slot="data"
    :to="isExternal || disabled ? undefined : localePath(to, locale)"
    :href="isExternal && !disabled ? to : undefined"
    :target
    :external="isExternal"
    custom
  >
    <slot
      v-if="custom"
      v-bind="{ ...$attrs, ...data, href: disabled ? undefined : getURLFromHref(data.href).href }"
    />
    <a
      v-else
      :data-test-id="dataTestId"
      v-bind="$attrs"
      :href="disabled ? undefined : getURLFromHref(data.href).href"
      :class="[
        {
          'router-link-active active-route': data.isActive,
          'router-link-exact-active': data.isExactActive,
        },
      ]"
      @click.capture.prevent="onClick($event)"
    >
      <slot />
    </a>
  </NuxtLink>
</template>

<script setup lang="ts">
import type { RouteLocation, RouteLocationRaw } from 'vue-router'
import { useContent } from '~~/shared-components/content'

const props = withDefaults(
  defineProps<{
    to?: RouteLocation | RouteLocationRaw
    locale?: string
    external?: boolean
    dataTestId?: string
    custom?: boolean
    target?: '_blank' | '_parent' | '_self' | '_top'
    disabled?: boolean
  }>(),
  {
    to: '/',
    locale: undefined,
    external: false,
    dataTestId: undefined,
    custom: false,
    target: undefined,
    disabled: false,
  }
)

const isExternal = computed(() => {
  if (typeof props.to === 'string') {
    try {
      new URL(props.to)
      return true
    } catch (e) {}
  }
  return props.external
})

defineEmits(['mouseenter', 'mouseleave'])

const attrs = useAttrs()

const callBack = async () => {
  if (!props.disabled) {
    if (isExternal.value) {
      const noopener = (attrs.rel as string)?.includes('noopener')
      const noreferrer = (attrs.rel as string)?.includes('noreferrer')
      await appNavigateTo(props.to, {
        external: true,
        ...(props.target || noopener || noreferrer
          ? { open: { target: props.target!, windowFeatures: { noopener, noreferrer } } }
          : {}),
      })
    } else {
      if (props.to) {
        await appNavigateTo(
          localePath(props.to, props.locale),
          props.target ? { open: { target: props.target } } : undefined
        )
      }
    }
  }
}

const localePath = useLocalePath()
const router = useRouter()
if (typeof props.to === 'object' && 'params' in props.to && props.to.params) {
  const name = router.resolve(localePath(props.to, props.locale))?.meta.name
  Object.entries(props.to).forEach(([key, value]) => {
    name?.replaceAll(key, value)
  })
}
const editing = inject<Ref<boolean>>('editing', shallowRef(false))
const confirm = useConfirm()
const {
  $i18n: { t },
} = useNuxtApp()
const { hideEditTools, selectedBlock, selectedContent, oneClick } = useContent()
const blockKey = inject<Ref<UUID | undefined>>('block-key', shallowRef(undefined))

const onClick = async (e: MouseEvent) => {
  // Pokud není vybrána editace v ERP tak se nejprve vybere
  e.preventDefault()

  if (editing?.value && hideEditTools.value) {
    hideEditTools.value = false
    return
  }

  if (
    editing.value &&
    selectedContent.value &&
    !oneClick.value &&
    blockKey?.value !== selectedBlock.value?.key
  ) {
    oneClick.value = true
    return
  }

  if (editing?.value) {
    let allow = true
    allow = await new Promise<boolean>((resolve) => {
      confirm.require({
        group: 'dialog',
        message: t('Opravdu si přejete spustit interakci?'),
        header: t('Potvrzení'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: { label: t('Zrušit'), severity: 'secondary', outlined: true },
        acceptProps: { label: t('Spustit') },
        accept: () => resolve(true),
        reject: () => resolve(false),
      })
    })

    if (!allow) {
      // uživatel zrušil, nic dál
      return
    }
  }
  if (props.to && typeof props.to === 'object' && props.to.hash) {
    const id = props.to.hash.slice(1)
    if (id) {
      scrollToElement(id)
    }
  }
  callBack()
}
</script>
